import { useState, useEffect, useCallback } from 'react'
import { Facebook, Twitter, Linkedin, Instagram, Download, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useParams, useNavigate } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Pagination from '@/components/ui/Pagination'
import { useFilterGalleries, useSearchGalleries } from '@/lib/api/hooks/gallery'
// import { useFilterEvents } from '@/lib/api/hooks/event'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import RecentArticles from '@/components/sections/RecentArticles'

import Search from '@/components/ui/Search'

import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'

import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'

import FILTER_SECTIONS from '@/components/pages/Media/PhotoGallery/FilterModule/FILTER_SECTIONS'

import FilterModule from '@/components/ui/FilterModule'
import { toast } from 'sonner'


interface Photo {
    id: string
    src: string
    title: string
    category: string
    year: string
    month: string
    isKeyHighlight: boolean
}

interface ModalPhoto extends Photo {
    index: number
}

// Helper function to extract year and month from date string
const extractYearMonth = (dateString: string | null): { year: string; month: string } => {
    if (!dateString) {
        const now = new Date()
        return {
            year: now.getFullYear().toString(),
            month: now.toLocaleString('default', { month: 'long' })
        }
    }

    const date = new Date(dateString)
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']

    return {
        year: date.getFullYear().toString(),
        month: months[date.getMonth()] || 'Unknown'
    }
}

export default function PhotoGalleryImages() {
    const { folderId } = useParams<{ folderId: string }>()
    const resolvedFolderId = folderId ?? ''
    const navigate = useNavigate()
    const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState(false)
    const [modalPhoto, setModalPhoto] = useState<ModalPhoto | null>(null)
    const [touchStart, setTouchStart] = useState<number | null>(null)
    const [touchEnd, setTouchEnd] = useState<number | null>(null)

      // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size = 20,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


    // Fetch galleries for this folder with pagination using public endpoint
    const { data: galleriesResponse, isLoading: galleriesLoading, isError: galleriesError, refetch: refetchGalleries } = useFilterGalleries(
        { page, size },
        { ...apiFilters, folderId: resolvedFolderId }
    )

      // ── Search query ─────────────────────
      const { data: searchData, isLoading: isSearchLoading, isError: searchError, refetch: refetchSearch } = useSearchGalleries(
        { page, size },
        searchTerm
      )
      
      // Get galleries from flat response (new API structure)
      const galleryData = mode === 'search' ? searchData : galleriesResponse
      
      const isLoading = mode === 'search' ? isSearchLoading : galleriesLoading
      const error = mode === 'search' ? searchError : galleriesError
      const refetchData = mode === 'search' ? refetchSearch : refetchGalleries
      const totalPages = galleryData?.data?.totalPage || 1
      const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS)
      const emptyMessage = mode === 'search' ? 'No photos found for this search query.' : mode === "filter" ? 'No photos found for this filter.' : 'No photos found in this folder.'
      
    
      const galleries = galleryData?.data?.data || []
    
      // Get folder name from first gallery item
    const folderName = galleries.length > 0 ? galleries[0].folderName : 'Gallery'


    // Transform galleries into photos array
    const photos: Photo[] =   Array.isArray(galleries) && galleries.length > 0 ? galleries
            .filter((gallery) => gallery.photoUrl)
            .map((gallery) => {
                const { year, month } = extractYearMonth(gallery.datePublished)
                return {
                    id: gallery.photoGalleryId,
                    src: gallery.photoUrl!,
                    title: gallery.title,
                    category: gallery.folderName,
                    year,
                    month,
                    isKeyHighlight: gallery.isKeyHighlight
                }
            }) || [] : []

    

    // Handle photo selection
    const handlePhotoSelect = (photoId: string) => {
        setSelectedPhotos(prev =>
            prev.includes(photoId)
                ? prev.filter(id => id !== photoId)
                : [...prev, photoId]
        )
    }

    // Handle select all
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedPhotos([])
        } else {
            setSelectedPhotos(photos.map(photo => photo.id))
        }
        setSelectAll(!selectAll)
    }

    // Update selectAll state when individual photos are selected
    useEffect(() => {
        if (photos.length === 0) {
            setSelectAll(false)
            return
        }
        const allCurrentSelected = photos.every(photo => selectedPhotos.includes(photo.id))
        const someSelected = photos.some(photo => selectedPhotos.includes(photo.id))
        setSelectAll(allCurrentSelected && someSelected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPhotos])

    // Handle photo modal
    const handlePhotoClick = (photo: Photo, index: number) => {
        setModalPhoto({ ...photo, index })
    }

    const handleModalNavigation = useCallback((direction: 'prev' | 'next') => {
        if (!modalPhoto) return

        let newIndex = modalPhoto.index + (direction === 'next' ? 1 : -1)
        if (newIndex < 0) newIndex = photos.length - 1
        if (newIndex >= photos.length) newIndex = 0

        const newPhoto = photos[newIndex]
        setModalPhoto({ ...newPhoto, index: newIndex })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalPhoto])

    // Handle keyboard navigation in modal
    useEffect(() => {
        if (!modalPhoto) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                handleModalNavigation('prev')
            } else if (e.key === 'ArrowRight') {
                handleModalNavigation('next')
            } else if (e.key === 'Escape') {
                setModalPhoto(null)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [modalPhoto, handleModalNavigation])

    // Handle swipe gestures
    const minSwipeDistance = 50

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null)
        setTouchStart(e.targetTouches[0].clientX)
    }

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX)
    }

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd || !modalPhoto) return
        const distance = touchStart - touchEnd
        const isLeftSwipe = distance > minSwipeDistance
        const isRightSwipe = distance < -minSwipeDistance

        if (isLeftSwipe) {
            handleModalNavigation('next')
        }
        if (isRightSwipe) {
            handleModalNavigation('prev')
        }
    }

    // Handle download
    const [isDownloading, setIsDownloading] = useState(false)

    const handleDownload = async () => {
        if (selectedPhotos.length === 0) {
            alert('Please select photos to download')
            return
        }

        setIsDownloading(true)

        try {
            // Import download utility dynamically
            const { downloadImage } = await import('../../../../utils/download')

            // Download selected photos
            for (let i = 0; i < selectedPhotos.length; i++) {
                const photoId = selectedPhotos[i]
                const photo = photos.find(p => p.id === photoId)
                if (photo) {
                    await downloadImage(photo.src, photo.title || `photo_${photoId}`)

                    // Small delay between downloads
                    if (i < selectedPhotos.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500))
                    }
                }
            }

            toast.success(`Successfully downloaded ${selectedPhotos.length} photo(s)`)
            setSelectedPhotos([])
            setSelectAll(false)
        } catch (error) {
            console.error('Download error:', error)
            toast.error('Some files failed to download. Please try again.')
        } finally {
            setIsDownloading(false)
        }
    }

    const breadcrumbItems = [
        { label: 'Home', href: '/' },
        { label: 'Photo Gallery', href: '/media/photo-gallery' },
        { label: folderName, href: '', active: true }
    ]

    if (!folderId) {
        return (
            <div className="text-center py-12">
                <p className="text-lg text-gray-500">Folder not found</p>
                <button
                    onClick={() => navigate('/media/photo-gallery')}
                    className="mt-4 px-4 py-2 bg-wine-100 text-white rounded-lg hover:bg-wine-200"
                >
                    Back to Folders
                </button>
            </div>
        )
    }

    return (
        <motion.div
            className="bg-white min-h-screen"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
        >
            {/* Breadcrumb */}
            <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-5" variants={fadeUp}>
                <Breadcrumb items={breadcrumbItems} />
            </motion.div>

            {/* Main Content */}
            <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
                <motion.div
                    className="flex flex-col lg:flex-row gap-12"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportReveal}
                >
                    {/* Sidebar */}
                    <div className="w-full lg:w-96 flex flex-col gap-12">
                   

                  

                        {/* Recent Articles */}
                        <RecentArticles />

                        {/* Social Links */}
                        <div className="flex flex-col gap-4">
                            <h3 className="text-lg text-gray-500 font-['PT_Serif']">OUR SOCIALS</h3>
                            <div className="flex gap-2">
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Facebook className="w-5 h-5 text-gray-800" />
                                </button>
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Twitter className="w-5 h-5 text-gray-800" />
                                </button>
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Instagram className="w-6 h-6 text-gray-800" />
                                </button>
                                <button className="w-6 h-6 hover:opacity-70">
                                    <Linkedin className="w-6 h-6 text-gray-800" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Photo Gallery Grid */}
                    <div className="flex-1 flex flex-col gap-6">
                         {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search photos..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

                        <div className="flex flex-col gap-6">
                            {isLoading ? (
                                <LoadingSpinner message="Loading images..." className="py-12" />
                            ) : error ? (
                                <div className="text-center flex flex-col items-center justify-center gap-4 py-12">
                                    <p className="text-lg text-gray-500">Failed to load photos. Please try again.</p>
                                    <button
                                        onClick={() => refetchData()}
                                        className="mt-4 px-4 py-2 bg-wine-100 text-white rounded-lg hover:bg-wine-200"
                                    >
                                        Back to Folders
                                    </button>
                                </div>
                            ) : photos.length === 0 ? (
                                <div className="text-center flex flex-col items-center justify-center gap-4 py-12">
                                    <p className="text-lg text-gray-500">{emptyMessage}</p>
                                    <button
                                        onClick={() => navigate('/media/photo-gallery')}
                                        className="mt-4 px-4 py-2 bg-wine-100 text-white rounded-lg hover:bg-wine-200"
                                    >
                                        Back to Folders
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Category Header with Select All */}
                                    <div className="">
                                        <div className="bg-gray-50 flex items-center justify-between p-2 rounded">
                                            <div className="flex items-center gap-4">
                                                <button
                                                    onClick={() => navigate('/media/photo-gallery')}
                                                    className="text-gray-700 hover:text-gray-900 flex items-center gap-2"
                                                >
                                                    ←
                                                </button>
                                                <h1 className="text-lg font-normal font-['PT_Serif'] text-gray-900">
                                                    {folderName}
                                                </h1>
                                            </div>
                                            <div className="flex items-center gap-4">
<div className="flex items-center gap-2">
                                                <span className="text-base text-gray-900">Select all</span>
                                                <div className="p-1 flex items-center justify-center">
                                                     <input type="checkbox"
                                                        checked={selectAll}
                                                        onChange={handleSelectAll}
                                                        className={`w-4 h-4 rounded-sm border-wine-100 border text-wine-100 focus:ring-wine-100 cursor-pointer accent-wine-100  ${selectAll ? 'bg-wine-100 border-wine-100' : 'bg-white border-gray-400'}`}
                                                    />
                                                    {/* <button
                                                        onClick={handleSelectAll}
                                                        className={`w-4 h-4 rounded-sm border-2 ${selectAll
                                                            ? 'bg-wine-100 border-wine-100'
                                                            : 'border-gray-400'
                                                            }`}
                                                    >
                                                        {selectAll && (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <div className="w-2 h-1 bg-white"></div>
                                                            </div>
                                                        )}
                                                    </button> */}
                                                </div>
                                            </div>
                                                  {/* Download Button */}
                        <button
                            onClick={handleDownload}
                            disabled={selectedPhotos.length === 0 || isDownloading}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium ${selectedPhotos.length > 0 && !isDownloading
                                ? 'bg-wine-100 text-white hover:bg-wine-200'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            <Download className="w-5 h-5" />
                            {isDownloading
                                ? 'Downloading...'
                                : `Download Selected (${selectedPhotos.length})`}
                        </button>
                                            </div>
                                            
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
                                        {photos.map((photo, index) => (
                                            <div
                                                key={photo.id}
                                                className="relative group shadow-lg flex flex-col hover:shadow-xl transition-shadow"
                                            >
                                                <div className="relative flex-1">
                                                    <img
                                                        src={photo.src}
                                                        alt={photo.title}
                                                        className="w-full h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                                        onClick={() => handlePhotoClick(photo, index)}
                                                        onError={(e) => {
                                                            e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                                                        }}
                                                    />
                                                    {/* Key Highlight Badge */}
                                                    {photo.isKeyHighlight && (
                                                        <span className="absolute top-2 left-2 text-xs bg-wine-100 text-white px-2 py-1 rounded-full font-medium">
                                                            Key Highlight
                                                        </span>
                                                    )}
                                                    {/* Selection Checkbox */}
                                                    <div className="absolute top-2 right-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handlePhotoSelect(photo.id)
                                                            }}
                                                            className={`w-5 h-5 rounded-sm border-2 ${selectedPhotos.includes(photo.id)
                                                                ? 'bg-wine-100 border-wine-100'
                                                                : 'bg-white border-gray-400'
                                                                } hover:border-wine-100 transition-colors`}
                                                        >
                                                            {selectedPhotos.includes(photo.id) && (
                                                                <div className="w-full h-full flex items-center justify-center">
                                                                    <div className="w-2 h-1 bg-white"></div>
                                                                </div>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="h-12 bg-white relative flex-shrink-0">
                                                    <div className="absolute left-4 top-3 text-lg font-medium text-gray-900 truncate max-w-[80%]">
                                                        {photo.title}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                  
                                        <div className=" mt-6">
                                            <Pagination
                                                currentPage={page}
                                                totalPages={totalPages}
                                                onPageChange={setPage}
                                            />
                                        </div>
                                  
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Photo Modal */}
            {modalPhoto && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                    onClick={() => setModalPhoto(null)}
                >
                    <div
                        className="relative max-w-4xl max-h-full p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setModalPhoto(null)}
                            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Navigation Buttons */}
                        <button
                            onClick={() => handleModalNavigation('prev')}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                            onClick={() => handleModalNavigation('next')}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>

                        <img
                            src={modalPhoto.src}
                            alt={modalPhoto.title}
                            className="max-w-full max-h-[90vh] object-contain"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        />

                        <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded">
                            <h3 className="text-lg font-medium mb-2">{modalPhoto.title}</h3>
                            <div className="flex justify-between items-center">
                                <span className="text-sm">{modalPhoto.year} - {modalPhoto.month}</span>
                                <span className="text-sm">Image {modalPhoto.index + 1} of {photos.length}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    )
}
